// TelcoTrack — parsování Ceník_Protelco.xlsx (všechny listy) z matic buněk

import type { ProtelcoCatalogItem } from '../constants/ppd-report.constants';

function cellStr(v: unknown): string {
  if (v == null) return '';
  return String(v).trim();
}

function isSapValue(v: unknown): boolean {
  if (typeof v === 'number' && Number.isFinite(v)) return v >= 10000 || (v >= 1000 && v < 10000);
  const s = cellStr(v).replace(/\s/g, '');
  return /^\d{5,7}$/.test(s) || /^HZS\d+$/i.test(s);
}

function normalizeSap(v: unknown): string {
  if (typeof v === 'number' && Number.isFinite(v)) return String(Math.trunc(v));
  return cellStr(v).replace(/\s/g, '');
}

function parsePrice(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const s = cellStr(v).replace(/\s/g, '').replace(',', '.');
  if (!s || s.toLowerCase().includes('x') || s.toLowerCase().includes('pauš')) return null;
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

function findHeaderRow(matrix: unknown[][]): number {
  for (let i = 0; i < Math.min(matrix.length, 15); i++) {
    const row = matrix[i] ?? [];
    const joined = row.map(cellStr).join(' ').toLowerCase();
    if (joined.includes('sap') && (joined.includes('název') || joined.includes('nazev'))) {
      return i;
    }
  }
  return -1;
}

function colIndex(row: unknown[], ...needles: string[]): number {
  const lower = row.map((c) => cellStr(c).toLowerCase());
  for (let i = 0; i < lower.length; i++) {
    const h = lower[i];
    if (needles.every((n) => h.includes(n))) return i;
  }
  return -1;
}

/**
 * Parsuje jeden list ceníku (matice buněk).
 */
export function parseProtelcoCatalogSheet(sheetName: string, matrix: unknown[][]): ProtelcoCatalogItem[] {
  const items: ProtelcoCatalogItem[] = [];
  const headerIdx = findHeaderRow(matrix);
  if (headerIdx < 0) return items;

  const header = matrix[headerIdx] ?? [];
  const sapCol = colIndex(header, 'sap') >= 0 ? colIndex(header, 'sap') : 0;
  let nameCol = colIndex(header, 'název');
  if (nameCol < 0) nameCol = colIndex(header, 'nazev');
  if (nameCol < 0) nameCol = 1;
  let unitCol = colIndex(header, 'jednot');
  if (unitCol < 0) unitCol = colIndex(header, 'měr');
  if (unitCol < 0) unitCol = 2;
  let priceCol = colIndex(header, 'standard');
  if (priceCol < 0) {
    for (let c = 3; c < header.length; c++) {
      const h = cellStr(header[c]).toLowerCase();
      if (h.includes('cena') || h.includes('standard') || h === '') {
        const sample = matrix[headerIdx + 1]?.[c];
        if (parsePrice(sample) != null) {
          priceCol = c;
          break;
        }
      }
    }
  }
  if (priceCol < 0) priceCol = 4;

  for (let i = headerIdx + 1; i < matrix.length; i++) {
    const row = matrix[i] ?? [];
    if (!isSapValue(row[sapCol])) continue;
    const sap = normalizeSap(row[sapCol]);
    const name = cellStr(row[nameCol]);
    const unit = cellStr(row[unitCol]) || 'ks';
    const unitPrice = parsePrice(row[priceCol]);
    if (unitPrice == null) continue;
    items.push({ sap, name, unit, unitPrice, sheetName });
  }

  return items;
}

/**
 * Sloučí položky ze všech listů; při duplicitě SAP vyhrává pozdější list.
 */
export function mergeProtelcoCatalogSheets(
  sheets: Array<{ name: string; matrix: unknown[][] }>,
): { items: ProtelcoCatalogItem[]; duplicateSaps: string[] } {
  const bySap = new Map<string, ProtelcoCatalogItem>();
  const seen = new Set<string>();
  const duplicateSaps: string[] = [];

  for (const { name, matrix } of sheets) {
    for (const item of parseProtelcoCatalogSheet(name, matrix)) {
      if (bySap.has(item.sap)) {
        if (!seen.has(item.sap)) {
          duplicateSaps.push(item.sap);
          seen.add(item.sap);
        }
      }
      bySap.set(item.sap, item);
    }
  }

  return { items: [...bySap.values()], duplicateSaps };
}
